import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, MessageCircle, Pencil } from 'lucide-react';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { cn } from '../../lib/utils';
import userTeamService from '../../services/userTeamService';
import { isSupportChatTypingFresh } from '../../services/supportChatService';

function formatThreadTime(lastMessageAt) {
  const date = lastMessageAt?.toDate ? lastMessageAt.toDate() : lastMessageAt ? new Date(lastMessageAt) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

function formatRoleLabel(role) {
  if (!role) return '';
  return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Super admin conversation list, most recent first, with a "new conversation"
 * picker so admins can start a chat with any user before they message first.
 */
const AdminChatInbox = ({ threads, selectedThreadEmail, onSelectThread, currentUserEmail }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const [now, setNow] = useState(() => Date.now());

  // Tick while any typing heartbeat is fresh so stale "typing…" previews expire.
  const hasFreshTyping = (threads || []).some((t) => isSupportChatTypingFresh(t.userTypingAt, now));
  useEffect(() => {
    if (!hasFreshTyping) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1500);
    return () => clearInterval(interval);
  }, [hasFreshTyping]);

  const recipients = useMemo(
    () => (showPicker ? userTeamService.getSupportChatRecipients(currentUserEmail) : []),
    [showPicker, currentUserEmail]
  );

  const filteredRecipients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return recipients;
    return recipients.filter(
      (r) => r.name.toLowerCase().includes(term) || r.email.includes(term)
    );
  }, [recipients, search]);

  const openPicker = () => {
    setSearch('');
    setShowPicker(true);
  };

  const pickRecipient = (email) => {
    setShowPicker(false);
    setSearch('');
    onSelectThread(email);
  };

  if (showPicker) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-border">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setShowPicker(false)}
            aria-label="Back to conversations"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search users…"
            className="h-8"
            autoFocus
          />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {filteredRecipients.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-muted-foreground">
              No matching users.
            </div>
          ) : (
            filteredRecipients.map((recipient) => (
              <button
                key={recipient.email}
                type="button"
                onClick={() => pickRecipient(recipient.email)}
                className="w-full text-left px-3 py-2.5 border-b border-border hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-foreground truncate">{recipient.name}</span>
                  {recipient.role && (
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {formatRoleLabel(recipient.role)}
                    </span>
                  )}
                </div>
                <span className="block text-xs text-muted-foreground truncate mt-0.5">
                  {recipient.email}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Conversations
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={openPicker}
          aria-label="Start a new conversation"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
      {!threads?.length ? (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
          <MessageCircle className="h-5 w-5" />
          No conversations yet. Start one with the pencil icon, or wait for a user to message you.
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          {threads.map((thread) => {
            const unread = Number(thread.unreadForAdmin) || 0;
            const isSelected = thread.id === selectedThreadEmail;
            const isTyping = isSupportChatTypingFresh(thread.userTypingAt, now);
            return (
              <button
                key={thread.id}
                type="button"
                onClick={() => onSelectThread(thread.id)}
                className={cn(
                  'w-full text-left px-3 py-2.5 border-b border-border focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring',
                  isSelected ? 'bg-muted' : 'hover:bg-muted/50'
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={cn('text-sm truncate', unread > 0 ? 'font-semibold text-foreground' : 'text-foreground')}>
                    {thread.userName || thread.userEmail || thread.id}
                  </span>
                  <span className="text-[11px] text-muted-foreground shrink-0">
                    {formatThreadTime(thread.lastMessageAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span
                    className={cn(
                      'text-xs truncate',
                      isTyping ? 'italic text-muted-foreground' : unread > 0 ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {isTyping
                      ? 'typing…'
                      : `${thread.lastMessageSender === 'admin' ? 'You: ' : ''}${thread.lastMessagePreview || '—'}`}
                  </span>
                  {unread > 0 && (
                    <Badge className="shrink-0 h-5 min-w-5 px-1.5 justify-center">{unread > 99 ? '99+' : unread}</Badge>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminChatInbox;
